import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Award, CheckCircle, ArrowRight, Hammer, ShoppingCart } from 'lucide-react';

// --- CONFIGURATION ---
const POWER_TOOLS_ID = 25; 

// ----------------------
// Types
// ----------------------
type Product = {
  id: number;
  name: string;
  price: number;
  oldPrice: number;
  discount: string;
  imageUrl: string;
  rating: string;
  reviews: number;
  categoryName?: string;
};

// ----------------------
// Product Card Component
// ----------------------
const ProductListingCard: React.FC<{ product: Product }> = ({ product }) => {
  
  const formatPrice = (p: number) => 
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(p);

  return (
    <article 
        className="group relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 flex flex-col h-full hover:-translate-y-1 cursor-pointer"
        itemScope 
        itemType="https://schema.org/Product"
    >
      
      {/* 1. Image Area */}
      <div className="relative w-full aspect-[4/3] bg-gray-50 flex items-center justify-center overflow-hidden p-6">
        
        {/* Category Badge */}
        <div className="absolute top-0 left-0 bg-teal-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-br-xl z-20 shadow-sm flex items-center gap-1 uppercase tracking-wider">
           <Hammer className="w-3 h-3" />
           {product.categoryName || 'POWER TOOL'}
        </div>

        {/* Hot Deal Tag */}
        {product.id % 2 === 0 && (
           <div className="absolute top-0 right-0 bg-red-50 text-red-600 text-[10px] font-bold px-2 py-1 rounded-bl-xl border border-red-100 z-10">
             HOT DEAL
           </div>
        )}

        <img 
          src={product.imageUrl}
          alt={product.name}
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110 mix-blend-multiply"
          loading="lazy"
          itemProp="image"
          onError={(e) => {e.currentTarget.src = 'https://via.placeholder.com/300?text=TradyFrog'}}
        />

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-teal-900/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

        {/* Action Buttons (Slide Up) */}
        <div className="absolute inset-x-0 bottom-4 px-4 flex items-center justify-center 
                        translate-y-12 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 
                        transition-all duration-300 ease-out z-20">
            <a 
                href={`/product/${product.id}`} 
                className="w-full text-center py-2.5 text-xs font-bold rounded-lg text-white bg-teal-600 hover:bg-teal-700 shadow-lg transition-colors flex items-center justify-center gap-2 uppercase"
            >
                View Details <ArrowRight className="w-3 h-3" />
            </a>
        </div>
      </div>
      
      {/* 2. Product Details */}
      <div className="p-4 flex flex-col flex-grow border-t border-gray-50">
        
        {/* Rating & Verified Badge */}
        <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-1 text-[10px] text-gray-500 font-medium uppercase tracking-wide">
                INDUSTRIAL <CheckCircle className="w-3 h-3 text-teal-500" />
            </div>
            
            <div className="flex items-center gap-1 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded text-[10px] font-bold text-amber-700">
                {product.rating} <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
            </div>
        </div>

        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mb-3 group-hover:text-teal-600 transition-colors">
            <a href={`/product/${product.id}`} itemProp="name">{product.name}</a>
        </h3>
        
        <div className="mt-auto pt-3 border-t border-dashed border-gray-200 flex items-center justify-between">
            <div className="flex flex-col">
                <span className="text-lg font-bold text-gray-900 leading-none">
                    {formatPrice(product.price)}
                </span>
                {product.oldPrice > product.price && (
                    <span className="text-xs text-gray-400 line-through mt-1">
                        {formatPrice(product.oldPrice)}
                    </span>
                )}
            </div>
            
            <div className="bg-red-50 text-red-600 text-[10px] font-bold px-2 py-1 rounded border border-red-100">
                {product.discount}
            </div>
        </div>
      </div>
    </article>
  );
};

// ----------------------
// Main Section Component
// ----------------------
const PowerToolsCards: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPowerTools = async () => {
      try {
        const response = await axios.get(`http://localhost:2026/api/products/parent-category/${POWER_TOOLS_ID}`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching power tools:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPowerTools();
  }, []);

  if (loading) return (
     <div className="w-full h-64 flex items-center justify-center">
         <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
     </div>
  );

  return (
    <section className="bg-white py-12 sm:py-16 border-t border-gray-100">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <header className="flex flex-col sm:flex-row justify-between items-end mb-8 border-b border-gray-100 pb-4">
            <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight uppercase flex items-center gap-2">
                    <Hammer className="w-6 h-6 text-teal-600" />
                    Power Tools <span className="text-teal-600">& Equipment</span>
                </h2>
                <p className="text-sm text-gray-500 mt-1">Professional grade tools for industrial use</p>
            </div>
        </header>
        
        {products.length === 0 ? (
             <div className="text-center py-20 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                 <div className="text-4xl mb-4 opacity-50">🛠️</div>
                 <h3 className="text-lg font-bold text-gray-800">No Power Tools Found</h3>
                 <p className="text-sm text-gray-500 mt-2">Check back later for new inventory.</p>
             </div>
        ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
                {products.map((product) => (
                    <ProductListingCard key={product.id} product={product} />
                ))}
            </div>
        )}

      </div>
    </section>
  );
};

export default PowerToolsCards;